import React, { useEffect, useState } from "react";
import { api, resolveImg } from "../../api";
import { invalidateCache } from "../../useData";

// Reusable editor for lists of {year, <valueKey>} rows.
// Used for Books, Awards, Solo Exhibitions, Selected Exhibitions,
// Publications and Education (CV) on the About page.
const PairListEditor = ({ label, items, valueKey, valuePlaceholder, yearPlaceholder, onChange, testid }) => {
  const list = Array.isArray(items) ? items : [];
  const setRow = (i, patch) => {
    const next = list.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...list, { year: "", [valueKey]: "" }]);
  return (
    <div className="mb-8 border-t border-[color:var(--line)] pt-6">
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-serif text-[13px] uppercase tracking-wider text-[color:var(--ink-mute)]">{label}</p>
        <span className="font-serif text-[11px] text-[color:var(--ink-mute)]">{list.length} rows</span>
      </div>
      <div className="space-y-2">
        {list.map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testid}-row-${i}`}>
            <input
              type="text"
              value={row.year || ""}
              onChange={(e) => setRow(i, { year: e.target.value })}
              placeholder={yearPlaceholder || "2025"}
              className="col-span-3 md:col-span-2 bg-transparent border border-[color:var(--line)] px-2 py-1.5 font-serif text-[13px] outline-none focus:border-[color:var(--ink)]"
              data-testid={`${testid}-year-${i}`}
            />
            <input
              type="text"
              value={row[valueKey] || ""}
              onChange={(e) => setRow(i, { [valueKey]: e.target.value })}
              placeholder={valuePlaceholder}
              className="col-span-7 md:col-span-8 bg-transparent border border-[color:var(--line)] px-2 py-1.5 font-serif text-[13px] outline-none focus:border-[color:var(--ink)]"
              data-testid={`${testid}-value-${i}`}
            />
            <div className="col-span-2 flex items-center justify-end gap-1 text-[color:var(--ink-mute)]">
              <button type="button" onClick={() => move(i, -1)} title="Move up" className="px-1.5 hover:text-[color:var(--ink)]">↑</button>
              <button type="button" onClick={() => move(i, 1)} title="Move down" className="px-1.5 hover:text-[color:var(--ink)]">↓</button>
              <button type="button" onClick={() => remove(i)} title="Remove" className="px-1.5 text-red-600 hover:text-red-800" data-testid={`${testid}-remove-${i}`}>×</button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 font-serif text-[13px] tlink" data-testid={`${testid}-add`}>
        + Add row
      </button>
    </div>
  );
};

// Reusable editor for lists of plain strings (Workshops, Clients, Editorial & Music, Selected Collaborations).
const StringListEditor = ({ label, items, placeholder, onChange, textarea = false, testid }) => {
  const list = Array.isArray(items) ? items : [];
  const setRow = (i, v) => onChange(list.map((r, idx) => (idx === i ? v : r)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...list, ""]);
  return (
    <div className="mb-8 border-t border-[color:var(--line)] pt-6">
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-serif text-[13px] uppercase tracking-wider text-[color:var(--ink-mute)]">{label}</p>
        <span className="font-serif text-[11px] text-[color:var(--ink-mute)]">{list.length} items</span>
      </div>
      <div className="space-y-2">
        {list.map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`${testid}-row-${i}`}>
            {textarea ? (
              <textarea
                value={row || ""}
                onChange={(e) => setRow(i, e.target.value)}
                placeholder={placeholder}
                rows={2}
                className="col-span-10 bg-transparent border border-[color:var(--line)] px-2 py-1.5 font-serif text-[13px] outline-none focus:border-[color:var(--ink)] resize-vertical"
                data-testid={`${testid}-value-${i}`}
              />
            ) : (
              <input
                type="text"
                value={row || ""}
                onChange={(e) => setRow(i, e.target.value)}
                placeholder={placeholder}
                className="col-span-10 bg-transparent border border-[color:var(--line)] px-2 py-1.5 font-serif text-[13px] outline-none focus:border-[color:var(--ink)]"
                data-testid={`${testid}-value-${i}`}
              />
            )}
            <div className="col-span-2 flex items-center justify-end gap-1 text-[color:var(--ink-mute)] pt-1">
              <button type="button" onClick={() => move(i, -1)} title="Move up" className="px-1.5 hover:text-[color:var(--ink)]">↑</button>
              <button type="button" onClick={() => move(i, 1)} title="Move down" className="px-1.5 hover:text-[color:var(--ink)]">↓</button>
              <button type="button" onClick={() => remove(i)} title="Remove" className="px-1.5 text-red-600 hover:text-red-800" data-testid={`${testid}-remove-${i}`}>×</button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 font-serif text-[13px] tlink" data-testid={`${testid}-add`}>
        + Add item
      </button>
    </div>
  );
};

// Editor for the artist profile (About page + Contact page info).
const FIELDS = [
  { key: "location", label: "Location", type: "input", placeholder: "Milan / Paris" },
  { key: "email", label: "Email", type: "input", placeholder: "alicelisistudio@gmail.com" },
  { key: "phone", label: "Phone", type: "input", placeholder: "+39 328 8179708" },
  { key: "instagram", label: "Instagram handle", type: "input", placeholder: "@allisonlisi_" },
  { key: "instagramUrl", label: "Instagram URL", type: "input", placeholder: "https://www.instagram.com/allisonlisi_/" },
  { key: "ga_id", label: "Google Analytics ID", type: "input", placeholder: "G-XXXXXXXXXX (leave empty to disable tracking)" },
];

const InfoEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);

  useEffect(() => {
    api.getSettings().then((d) => setData(d || {})).catch(() => setData({}));
  }, []);

  const update = (patch) => {
    setData((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const onPortrait = async (file) => {
    if (!file) return;
    setUploadingPortrait(true);
    try {
      const r = await api.upload(file);
      update({ portrait: r.url });
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingPortrait(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(data);
      invalidateCache();
      setDirty(false);
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (data === null) return <p className="p-8 font-serif text-[color:var(--ink-mute)]">Loading…</p>;

  const wordCount = (data.statement || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="max-w-4xl p-6 md:p-8 pb-24">
      <div className="mb-6">
        <p className="font-serif text-2xl">Info & About</p>
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mt-1">
          Public bio, contact information and every section of the About page.
        </p>
      </div>

      {/* Portrait */}
      <div className="mb-8">
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mb-2">Portrait</p>
        <div className="flex items-start gap-5">
          <div className="w-32 aspect-[3/4] bg-[color:var(--paper-2)] overflow-hidden flex-none">
            {data.portrait ? (
              <img src={resolveImg(data.portrait)} alt="portrait" className="w-full h-full object-cover" />
            ) : null}
          </div>
          <div className="space-y-3">
            <label className="font-serif text-[13px] tlink cursor-pointer inline-block">
              {uploadingPortrait ? "Uploading…" : (data.portrait ? "Replace portrait" : "Upload portrait")}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => { onPortrait(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="info-portrait-upload"
              />
            </label>
            {data.portrait && (
              <button
                onClick={() => update({ portrait: "" })}
                className="font-serif text-[13px] text-red-600 tlink block"
                type="button"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Statement / Bio */}
      <label className="block mb-8">
        <div className="flex items-baseline justify-between mb-2">
          <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Bio / Statement</span>
          <span className="font-serif text-[11px] text-[color:var(--ink-mute)]">{wordCount} words</span>
        </div>
        <textarea
          value={data.statement || ""}
          onChange={(e) => update({ statement: e.target.value })}
          rows={16}
          className="w-full bg-transparent border border-[color:var(--line)] p-3 font-serif text-[15px] leading-[1.6] resize-vertical focus:outline-none focus:border-[color:var(--ink)]"
          placeholder="Write your bio here — leave a blank line between paragraphs."
          data-testid="info-statement"
        />
        <p className="font-serif text-[11px] text-[color:var(--ink-mute)] mt-1 italic">
          Tip: use a blank line to separate paragraphs.
        </p>
      </label>

      {/* Simple fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mb-8">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">{f.label}</span>
            <input
              type="text"
              value={data[f.key] || ""}
              onChange={(e) => update({ [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="mt-1 w-full bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[15px]"
              data-testid={`info-${f.key}`}
            />
          </label>
        ))}
      </div>

      {/* About-page lists */}
      <p className="font-serif text-[15px] mb-1 mt-10">About page sections</p>
      <p className="font-serif text-[12px] text-[color:var(--ink-mute)] mb-4">
        Every list here maps 1:1 to a section on the public About page. Use “Upcoming” (or leave the year blank) for entries without a fixed date.
      </p>

      <PairListEditor
        label="Books"
        items={data.books}
        valueKey="title"
        yearPlaceholder="Upcoming / 2025"
        valuePlaceholder="Book title"
        onChange={(v) => update({ books: v })}
        testid="books"
      />
      <PairListEditor
        label="Selected Exhibitions"
        items={data.selectedExhibitions}
        valueKey="entry"
        yearPlaceholder="20 Sep 2025"
        valuePlaceholder="Venue, City, Country — Project"
        onChange={(v) => update({ selectedExhibitions: v })}
        testid="selected"
      />
      <PairListEditor
        label="Publications"
        items={data.publications}
        valueKey="title"
        yearPlaceholder="Aug 2025"
        valuePlaceholder="Publication (upcoming) — Project"
        onChange={(v) => update({ publications: v })}
        testid="publications"
      />
      <PairListEditor
        label="Education"
        items={data.cv}
        valueKey="entry"
        yearPlaceholder="2024–now"
        valuePlaceholder="Degree / school, city"
        onChange={(v) => update({ cv: v })}
        testid="cv"
      />
      <StringListEditor
        label="Workshops & Mentorships"
        items={data.workshops}
        placeholder="2025 — Workshop with …"
        onChange={(v) => update({ workshops: v })}
        testid="workshops"
      />
      <StringListEditor
        label="Commercial clients"
        items={data.clients}
        placeholder="Client name"
        onChange={(v) => update({ clients: v })}
        testid="clients"
      />
      <StringListEditor
        label="Editorial & Music"
        items={data.editorialMusic}
        placeholder="Name"
        onChange={(v) => update({ editorialMusic: v })}
        testid="editorial"
      />
      <StringListEditor
        label="Selected Collaborations (assistant to)"
        items={data.assistantTo}
        placeholder="Photographer name"
        onChange={(v) => update({ assistantTo: v })}
        testid="assistant"
      />

      {/* Save */}
      <div className="sticky bottom-0 bg-white border-t border-[color:var(--line)] pt-4 pb-4 mt-6 flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="btn-sober disabled:opacity-40"
          data-testid="info-save-button"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
        {dirty && <span className="font-serif text-[12px] text-[color:var(--ink-mute)]">Unsaved changes</span>}
      </div>
    </div>
  );
};

export default InfoEditor;
